import React, { useState } from 'react';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';

interface SidebarCardProps {
  /** Card title */
  title: string;
  /** Optional icon for the title */
  icon?: React.ReactNode;
  /** Whether the card is collapsible */
  collapsible?: boolean;
  /** Initial collapsed state */
  defaultCollapsed?: boolean;
  /** Card content */
  children: React.ReactNode;
  /** Additional class name */
  className?: string;
  /** Action button in header */
  action?: React.ReactNode;
}

/**
 * SidebarCard Component
 *
 * A reusable card component for sidebar sections.
 * Supports collapsible behavior and header actions.
 */
export const SidebarCard: React.FC<SidebarCardProps> = ({
  title,
  icon,
  collapsible = false,
  defaultCollapsed = false,
  children,
  className = '',
  action,
}) => {
  const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed);

  const handleToggle = () => {
    if (collapsible) {
      setIsCollapsed(!isCollapsed);
    }
  };

  return (
    <div className={`sidebar-card ${className}`}>
      <div
        className={`sidebar-card-header ${collapsible ? 'clickable' : ''}`}
        onClick={handleToggle}
        role={collapsible ? 'button' : undefined}
        tabIndex={collapsible ? 0 : undefined}
        aria-expanded={collapsible ? !isCollapsed : undefined}
      >
        <div className="sidebar-card-title">
          {icon && <span className="sidebar-card-icon">{icon}</span>}
          <span>{title}</span>
        </div>
        <div className="sidebar-card-header-right">
          {action && <div className="sidebar-card-action">{action}</div>}
          {collapsible && (
            <span className="sidebar-card-toggle">
              {isCollapsed ? (
                <ChevronDownIcon className="w-4 h-4" />
              ) : (
                <ChevronUpIcon className="w-4 h-4" />
              )}
            </span>
          )}
        </div>
      </div>
      {(!collapsible || !isCollapsed) && (
        <div className="sidebar-card-content">{children}</div>
      )}
    </div>
  );
};

export default SidebarCard;
